import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { reportBug } from '../../../store/bugSlice';

interface Props {
  projectId?: string;
  onCreated?: (bugId: string) => void;
  onCancel?: () => void;
}

const BugReportForm: React.FC<Props> = ({ projectId, onCreated, onCancel }) => {
  const dispatch = useAppDispatch();
  const { loading, error } = useAppSelector(s => s.bugs);

  const [form, setForm] = useState({
    title: '',
    description: '',
    severity: 'medium' as 'critical' | 'high' | 'medium' | 'low',
    reproductionSteps: '',
    environment: '',
    priority: 'medium' as 'critical' | 'high' | 'medium' | 'low',
    visibility: 'public_org' as 'private' | 'public_org' | 'public' | 'custom',
    storyPoints: 0,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(reportBug({
      title: form.title,
      description: form.description,
      impact: { severity: form.severity, affectedFeatures: [] },
      reproductionSteps: {
        steps: form.reproductionSteps
          ? form.reproductionSteps.split('\n').map(s => s.trim()).filter(Boolean)
          : ['Not provided'],
        environment: form.environment || undefined,
      },
      priority: form.priority,
      visibility: form.visibility,
      storyPoints: form.storyPoints || 0,
      owningProjectId: projectId,
      reportedInProjectId: projectId,
    } as any));

    if (reportBug.fulfilled.match(result)) {
      onCreated?.(result.payload._id);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-5 max-w-2xl">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Report a Bug</h2>
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
        <input
          name="title"
          value={form.title}
          onChange={handleChange}
          required
          placeholder="Short description of the bug"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
        <textarea
          name="description"
          value={form.description}
          onChange={handleChange}
          required
          rows={4}
          placeholder="What happened? What did you expect to happen?"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Steps to Reproduce</label>
        <textarea
          name="reproductionSteps"
          value={form.reproductionSteps}
          onChange={handleChange}
          rows={3}
          placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-400 mt-1">One step per line</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
        <input
          name="environment"
          value={form.environment}
          onChange={handleChange}
          placeholder="e.g. Chrome 124 / macOS / Production"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Severity *</label>
          <select
            name="severity"
            value={form.severity}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
          <select
            name="priority"
            value={form.priority}
            onChange={handleChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
        <select
          name="visibility"
          value={form.visibility}
          onChange={handleChange}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="private">Private (only me)</option>
          <option value="public_org">Organisation (default)</option>
          <option value="public">Public</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Story Points</label>
        <input
          name="storyPoints"
          type="number"
          min={0}
          value={form.storyPoints}
          onChange={e => setForm(f => ({ ...f, storyPoints: Number(e.target.value) }))}
          placeholder="Points awarded for fixing this bug"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <p className="text-xs text-gray-400 mt-1">Points earned by assignee when bug is resolved</p>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="bg-red-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
        >
          {loading ? 'Reporting…' : 'Report Bug'}
        </button>
      </div>
    </form>
  );
};

export default BugReportForm;
